import { LogLevel, logLevelName } from './LogLevel'
import type { ILogSink, LogRecord } from './types'

/**
 * 间接引用 console，避免被构建期 removeConsole 静态匹配删除。
 *
 * removeConsole 在语法层面匹配 `console.xxx(...)` 字面调用。这里通过 globalThis 间接取得
 * console 引用并以变量形式调用，使语法匹配失效，从而成为生产构建中唯一受控的输出点。
 */
const consoleRef: Console | undefined = (globalThis as { console?: Console }).console

/**
 * 将时间戳格式化为 HH:mm:ss.mmm。
 *
 * @param timestamp - performance.now() 毫秒值
 * @returns 形如 '13:24:05.123' 的时间串
 */
function formatTimestamp(timestamp: number): string {
  const totalMs = Math.max(0, Math.floor(timestamp))
  const ms = totalMs % 1000
  const totalSec = Math.floor(totalMs / 1000)
  const sec = totalSec % 60
  const min = Math.floor(totalSec / 60) % 60
  const hour = Math.floor(totalSec / 3600) % 24
  const p2 = (n: number) => String(n).padStart(2, '0')
  const p3 = (n: number) => String(n).padStart(3, '0')
  return `${p2(hour)}:${p2(min)}:${p2(sec)}.${p3(ms)}`
}

/**
 * 唯一的日志输出实现：按级别映射到 console.error/warn/info/debug，间接引用绕过 removeConsole。
 */
export class ConsoleSink implements ILogSink {
  /**
   * 输出一条日志记录，格式为 `[HH:mm:ss.mmm] [namespace] LEVEL message`。
   *
   * @param record - 待输出的日志记录
   */
  write(record: LogRecord): void {
    const out = consoleRef
    if (out == null) {
      return
    }
    const line =
      `[${formatTimestamp(record.timestamp)}] [` +
      `${record.namespace}] ${logLevelName(record.level)} ${record.message}`
    switch (record.level) {
      case LogLevel.ERROR:
        out.error(line)
        break
      case LogLevel.WARN:
        out.warn(line)
        break
      case LogLevel.INFO:
        out.info(line)
        break
      case LogLevel.DEBUG:
        out.debug(line)
        break
      default:
        out.log(line)
        break
    }
  }
}
